import React, { useRef, useMemo, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function StackedBarGraph({ data, isProdAgropec, onImageReady, text }) {
  const chartRef = useRef(null);
  const tooltipId = `chartjs-tooltip-${text.replace(/\s+/g, "-").toLowerCase()}`;

  const sortedData = useMemo(() => {
    let dataArray = Object.values(data);

    if (isProdAgropec) {
      dataArray = [...dataArray].sort((a, b) => b.valor - a.valor);
    }

    const combined = dataArray.map((elem) => ({
      alias: elem.apelido,
      comercial: elem.dados[0]["01 - Comercial"] ?? 0,
      subsistencia: elem.dados[0]["02 - Subsistência"] ?? 0,
    }));

    combined.sort(
      (a, b) => b.comercial + b.subsistencia - (a.comercial + a.subsistencia)
    );

    return {
      labels: combined.map((item) => item.alias),
      datasets: [
        {
          label: "Comercial",
          data: combined.map((item) => item.comercial),
          backgroundColor: "#F3E9D2",
        },
        {
          label: "Subsistência",
          data: combined.map((item) => item.subsistencia),
          backgroundColor: "#8B6E3B",
        },
      ],
    };
  }, [data, isProdAgropec]);

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      onComplete: () => {
        if (chartRef.current && onImageReady) {
          const imgBase64 = chartRef.current.canvas.toDataURL("image/png", 1.0);
          onImageReady(imgBase64);
        }
      },
    },
    plugins: {
      title: {
        display: true,
        text,
        font: { size: 12 },
        color: "#333",
        padding: { top: 10, bottom: 20 },
      },
      legend: {
        display: true,
        position: "bottom",
      },
      tooltip: {
        enabled: false,
        external: function (context) {
          let tooltipEl = document.getElementById(tooltipId);

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = tooltipId;
            tooltipEl.style.position = "absolute";
            tooltipEl.style.background = "rgba(255,255,255,0.95)";
            tooltipEl.style.border = "1px solid #ccc";
            tooltipEl.style.borderRadius = "4px";
            tooltipEl.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
            tooltipEl.style.padding = "8px";
            tooltipEl.style.pointerEvents = "none";
            tooltipEl.style.transition = "all 0.1s ease";
            tooltipEl.style.fontSize = "12px";
            document.body.appendChild(tooltipEl);
          }

          const { chart, tooltip } = context;

          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          const index = tooltip.dataPoints[0].dataIndex;
          const label = chart.data.labels[index];

          let currentData = null;

          Object.values(data).forEach((prod) => {
            if (prod.apelido === label) {
              currentData = prod;
            }
          });

          if (!currentData || !Array.isArray(currentData.dados)) {
            tooltipEl.style.opacity = 0;
            return;
          }

          tooltipEl.innerHTML = `
            <div style="font-family: sans-serif; font-size: 8px; color: #333;">
              <table style="border-collapse: collapse; width: 100%;">
                <thead>
                  <tr style="background-color: #f0f0f0;">
                    <th style="border: 1px solid #ccc; padding: 4px 8px; text-align: left;">Cultura</th>
                    <th style="border: 1px solid #ccc; padding: 4px 8px; text-align: right;">Comercial</th>
                    <th style="border: 1px solid #ccc; padding: 4px 8px; text-align: right;">Subsistência</th>
                  </tr>
                </thead>
                <tbody>
                  ${currentData.dados.map((item) => `
                    <tr>
                      <td style="border: 1px solid #ccc; padding: 4px 8px;">${item.nome}</td>
                      <td style="border: 1px solid #ccc; padding: 4px 8px; text-align: right;">${item['01 - Comercial'] ?? 0}</td>
                      <td style="border: 1px solid #ccc; padding: 4px 8px; text-align: right;">${item['02 - Subsistência'] ?? 0}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          `;

          const canvasRect = chart.canvas.getBoundingClientRect();
          const tooltipRect = tooltipEl.getBoundingClientRect();
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;

          let left = window.pageXOffset + canvasRect.left + tooltip.caretX - tooltipRect.width - 10;
          let top = window.pageYOffset + canvasRect.top + tooltip.caretY;

          if (left + tooltipRect.width > windowWidth) {
            left = windowWidth - tooltipRect.width - 10;
          }
          if (top + tooltipRect.height > windowHeight) {
            top = windowHeight - tooltipRect.height - 10;
          }
          if (top < 0) top = 10;

          tooltipEl.style.left = `${left}px`;
          tooltipEl.style.top = `${top}px`;
          tooltipEl.style.opacity = 1;
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        stacked: true,
        ticks: { color: "#333", font: { size: 9 } },
        grid: { drawBorder: false, color: "#ddd" },
      },
      y: {
        stacked: true,
        ticks: { color: "#333", font: { size: 9 } },
        grid: { display: false },
      },
    },
  };

  useEffect(() => {
    return () => {
      const el = document.getElementById(tooltipId);
      if (el) el.remove();
    };
  }, [tooltipId]);

  return <Bar ref={chartRef} data={sortedData} options={options} />;
}

export default StackedBarGraph;
